<template>
    <div class="lottie-player-container" :data-status="status">
        <div v-show="isLoading" class="lottie-loader-container">
            <span class="lottie-loader"></span>
        </div>
        <div v-show="isError" class="lottie-retry" @click="loadLottie"></div>
        <div class="lottie-player" ref="lottiePlayerContainer"></div>
    </div>
</template>

<script>
import lottie from 'lottie-web';
import Defer from '../Defer';
const {
    common: {
        utils: {
            lazyload: { mixins: lazyloadMixins }
        }
    }
} = window.FEGORender;

export default {
    name: 'lottie-player',
    mixins: [lazyloadMixins()],
    props: {
        defaultVisible: {
            type: Boolean,
            default: false
        },
        loop: {
            type: Boolean,
            default: false
        },
        path: {
            type: String,
            default: ''
        },
        autoplay: {
            type: Boolean,
            default: false
        },
        preload: {
            type: Boolean,
            default: false
        },
        renderer: {
            type: String,
            default: 'svg'
        },
        delay: {
            type: Number,
            default: 0
        },
        loopspan: {
            type: Number,
            default: 0
        },
        segments: {
            type: String,
            default: ''
        }
    },
    computed: {
        status() {
            if (!this.lazyloadHasReach) {
                return 'waiting';
            }
            if (this.isLoaded) {
                return 'loaded';
            }

            this.loadLottie();
            return 'loading';
        },
        segmentsArr() {
            if (!this.segments) {
                return [];
            }
            try {
                return JSON.parse(this.segments);
            } catch (err) {
                console.log(`get segmentsArr error ${this.segments} - ${err}`);
                return [];
            }
        }
    },
    data() {
        return {
            visible: Boolean(this.defaultVisible),
            anim: null,
            isLoading: false,
            isError: false,
            isLoaded: false,
            isLoadedPromise: null
        };
    },
    created() {
        if (this.preload) {
            //<link rel="preload" :href="path" as="fetch" type="application/json" crossorigin="anonymous">
            const link = document.createElement('link');

            link.rel = 'preload';
            link.href = this.path;
            link.as = 'fetch';
            link.type = 'application/json';
            link.crossOrigin = 'anonymous';

            document.getElementsByTagName('head')[0].appendChild(link);
            console.log(`preload ${this.path}`);
        }
    },
    mounted() {
        console.log('mounted', this.path);
    },
    // 在组件销毁时移除事件监听
    beforeDestroy() {
        if (this.anim && this.loop) {
            this.anim.removeEventListener('loopComplete');
        }
    },
    methods: {
        loadLottie() {
            //续渲染不处理了。不产生多于的SVG在静态，
            if (location.href.includes('pre_rendering') || window.__PRE_RENDERING__) {
                console.log('is pre render');
                return;
            }

            if (this.isLoading || this.isLoaded || !this.lazyloadHasReach) {
                return;
            }

            this.isLoading = true;
            this.isError = false;

            console.log(`===start loading lottie ${this.path}===`);

            try {
                //如果要延迟播放那么就要先把自动播放给停掉
                const autoplay = this.delay > 0 ? false : this.autoplay;
                let delayPlayIsFinished = false;
                const startTime = Number(new Date());

                //如果设置了loop间隔则也设置默认为没有loop,由complete来完成loop的行为
                const loop = this.loopspan > 0 ? false : this.loop;

                this.anim = lottie.loadAnimation({
                    container: this.$refs.lottiePlayerContainer,
                    renderer: this.renderer || 'svg',
                    loop,
                    autoplay,
                    path: this.path
                });

                this.isLoadedPromise = new Defer();

                //如果有超时间，那么在到时间且加载完成数据了。就直接开始播放，否则就等后面播放
                if (this.delay > 0) {
                    console.log(`${this.path} is delay`);
                    setTimeout(() => {
                        if (this.isLoaded) {
                            console.log(`${this.path} is delay play suncess`);

                            if (this.segmentsArr.length > 0) {
                                this.anim?.playSegments(this.segmentsArr, true);
                            } else {
                                this.anim?.play();
                            }
                            delayPlayIsFinished = true;
                        }
                    }, this.delay);
                }

                this.anim.addEventListener('DOMLoaded', () => {
                    this.isError = false;
                    this.isLoading = false;
                    this.isLoaded = true;
                    if (this.delay > 0) {
                        //有超时间，但加载的时间超过了延迟时间那就需要直接播放了
                        //时间不定完全准确所以配合delayPlayIsFinished
                        if (Number(new Date()) > startTime + this.delay && !delayPlayIsFinished) {
                            console.log(`${this.path} is delay before loaded`);
                            this.anim?.play();
                        }
                    }

                    if (this.segmentsArr.length > 0 && this.autoplay && this.delay === 0) {
                        this.anim?.playSegments(this.segmentsArr, true);
                    }

                    console.log(`${this.path} is DOMLoaded`);

                    // 发出加载完成事件
                    this.$emit('lottie-loaded');
                    this.isLoadedPromise.resolve();
                });

                //主要处理循环间隔
                let timer = null;
                this.anim.addEventListener('complete', () => {
                    clearTimeout(timer);
                    // 非循环动画在这里emit结束事件
                    this.$emit('lottie-complete');

                    if (this.loopspan > 0) {
                        timer = setTimeout(() => {
                            this.anim?.goToAndPlay(0, true);
                            console.log('loop span play ');
                        }, this.loopspan);
                    }
                });

                // 循环动画完成
                if (this.loop) {
                    this.anim.addEventListener('loopComplete', () => {
                        this.$emit('lottie-loop-complete');
                    });
                }

                this.anim.addEventListener('data_failed', (e) => {
                    this.isError = true;
                    this.isLoaded = false;
                    this.isLoading = false;
                    console.log(`${this.path} is element data_failed = ${e}`);
                });
            } catch (err) {
                console.log(`load lottie err ${err}`);
                this.isError = true;
                this.isLoading = false;
            }
        },
        /**
         * 打开弹窗
         * 用于【操作组件】交互
         */
        goToAndPlay(args) {
            const frameIndex = parseInt(args) || 0;
            this.anim?.resetSegments(true);
            this.anim?.goToAndPlay(frameIndex, true);
            console.log(`goToAndPlay(${frameIndex}, true)`);
        },
        goToAndStop(args) {
            const frameIndex = parseInt(args) || 0;
            this.anim?.resetSegments(true);
            this.anim?.goToAndStop(frameIndex, true);
            console.log(`goToAndStop(${frameIndex}, true)`);
        },
        playSegments(args) {
            const segments = JSON.parse(args);
            this.anim?.playSegments(segments, true);
            console.log(`playSegments(${segments}, true)`);
        },
        /**
         * 关闭弹窗
         * 用于【操作组件】交互
         */
        close() {
            this.visible = false;
        },
        onClick() {
            this.close();
        }
    }
};
</script>

<style scoped>
.lottie-loader-container {
    width: 48px;
    height: 48px;
    position: relative;
    top: 50%;
    left: 50%;
    z-index: 100;
    transform: translateX(-50%) translateY(-50%);
}

.lottie-player {
    height: 100%;
}

/* .lottie-loader {
    width: 48px;
    height: 48px;
    border: 5px solid #FFF;
    border-bottom-color: #ff6f25;
    border-radius: 50%;
    display: inline-block;
    box-sizing: border-box;
    animation: rotation 1s linear infinite;
} */

@keyframes rotation {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}
</style>
